import { METRIC_COMPARISON_OPTIONS } from 'components/Sidebar/RenderTunables/colorTagTunables/constants';
import milliseconds from 'date-fns/milliseconds';
import intervalToDuration from 'date-fns/intervalToDuration';
import _intersection from 'lodash/intersection';
import {
    AnalyticsGroupBy,
    AnalyticsProjection,
} from '@sprinklr/stories/analytics/AnalyticsRequest';
import AccountItem from '@sprinklr/stories/analytics/AccountItem';
import BulkItem, { AnalyticChartLabel } from '@sprinklr/stories/analytics/BulkItem';
import { uppercaseWithUnderscores } from 'utils/StringUtils/StringUtils';

export type ColorTagConditionMetric = {
    type: 'METRIC';
    name: string; // measurementName + '_' + aggregation
    comparison: '>' | '<' | '===' | '>=' | '<=';
    value: string | number | null | Duration;
};

export type ColorTagConditionDimension = {
    type: 'DIMENSION';
    name: string;
    comparison: 'IN' | 'NIN';
    value: string[];
};

export type ColorTagCondition = ColorTagConditionMetric | ColorTagConditionDimension;

// color tag conversion from pre April 2023 to current happens in DataStore -> WidgetImpl mapper
// https://sprinklr.atlassian.net/browse/DISPLAY-4840
export type ColorTag = {
    color: string;
    conditions?: Array<ColorTagCondition>;
};

export function getName(groupByOrProjection: AnalyticsGroupBy | AnalyticsProjection) {
    return (
        groupByOrProjection.details?.alternateHeading ??
        groupByOrProjection.details?.displayName ??
        groupByOrProjection?.details?.label ??
        groupByOrProjection.heading
    );
}

function isComparison(comparison: ColorTagConditionMetric['comparison']) {
    return METRIC_COMPARISON_OPTIONS.find(option => option.value === comparison);
}

function getDuration(inputMS: number): Duration {
    return intervalToDuration({ start: 0, end: inputMS });
}

function normalizeDurationAsMS(duration: Duration): number {
    return milliseconds(getDuration(milliseconds(duration)));
}

function getResolvedSourceDurationAsMS(metricValue, item) {
    const sourceDuration = getDuration(metricValue);
    const mappedSourceDuration = Object.keys(item.value).reduce(
        (prev, curr) => ({ ...prev, [curr]: sourceDuration?.[curr] }),
        {}
    );
    const resolvedSourceDuration = ['===', '>=', '<='].includes(item?.comparison)
        ? mappedSourceDuration
        : sourceDuration;

    return milliseconds(resolvedSourceDuration);
}

export function getColorTag({
    colorTags,
    rowValuesMap = null,
}: {
    colorTags: ColorTag[];
    rowValuesMap: { [key: string]: number | string | string[] };
}): ColorTag | undefined {
    // defensive check
    if (rowValuesMap === null) {
        return;
    }

    return (
        colorTags
            ?.filter(colorTag => {
                // defensive check
                if (!colorTag.conditions?.length) {
                    return false;
                }

                const conditionResults = colorTag.conditions.map(condition => {
                    const rowValue = rowValuesMap[condition.name];
                    if (rowValue === undefined) {
                        return false;
                    }

                    if (condition.type === 'METRIC') {
                        const isNumeric = ['number', 'undefined'].includes(typeof condition.value);

                        const sourceValue = isNumeric
                            ? rowValue
                            : getResolvedSourceDurationAsMS(rowValue, condition);

                        const targetValue = isNumeric
                            ? condition.value
                            : normalizeDurationAsMS(condition.value as Duration);

                        return (
                            isComparison(condition?.comparison as any) &&
                            condition.value !== null &&
                            rowValue !== null &&
                            eval(`${sourceValue}${condition.comparison}${targetValue}`)
                        );
                    } else {
                        const value = getResolvedDimensionValue(rowValue);

                        const conditionValues = condition.value.map(val =>
                            uppercaseWithUnderscores(val + '')
                        );

                        const values = Array.isArray(value)
                            ? _intersection(conditionValues, value)?.length > 0
                            : conditionValues.includes(value);
                        return condition.comparison === 'IN' && values;
                    }
                });

                return conditionResults.every(result => result);
            })
            // last match wins so we grab the last item here
            ?.at(-1)
    );
}

export function getResolvedDimensionValue(
    rowValue: string[] | AccountItem | AnalyticChartLabel
): string | string[] {
    // uppercase each item and return
    if (Array.isArray(rowValue)) {
        return rowValue.map(val => uppercaseWithUnderscores(val + ''));
    }

    let value = rowValue;

    // get the correct id from BulkItem and its variants
    if (rowValue instanceof BulkItem) {
        if (rowValue instanceof AccountItem) {
            value = rowValue.accountId;
        } else {
            value = rowValue.id;
        }
    }

    // make sure we uppercase the value
    return uppercaseWithUnderscores(value + '');
}
